import { useState, useEffect, useCallback } from 'react'
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
  where,
  getDocs,
  setDoc,
  getDoc,
} from 'firebase/firestore'
import { getFirebaseDb, isFirebaseAvailable } from '../lib/firebaseInit'
import type { Message, User } from '../schemas/chat.schema'
import { chatConfig } from '../config/firebase.config'
import {
  CHAT_ERRORS,
  CHAT_FIELDS,
  CHAT_PATHSEGMENTS,
  CHATROOM_PATH,
} from '@/constants/chat'
import { SORTING } from '@/constants'

export const useChat = (
  roomId: string,
  currentUser: User,
  isSSAdmin: boolean = false,
  localStorageChatAdminId?: string
) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [typing, setTyping] = useState(false)
  const [roomExists, setRoomExists] = useState(true)
  const [isAuthorized, setIsAuthorized] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const [roomChatAdminId, setRoomChatAdminId] = useState<string | null>(null)

  useEffect(() => {
    if (!roomId) return

    if (!isFirebaseAvailable()) {
      setError('Chat service is currently unavailable. Please try again later.')
      setLoading(false)
      return
    }

    const db = getFirebaseDb()
    if (!db) {
      setError('Chat service is currently unavailable. Please try again later.')
      setLoading(false)
      return
    }

    const messagesRef = collection(
      db,
      CHATROOM_PATH,
      roomId,
      CHAT_PATHSEGMENTS.MESSAGES
    )
    const firebaseQuery = query(
      messagesRef,
      orderBy(CHAT_FIELDS.TIMESTAMP, SORTING.ASC)
    )

    const unsubscribe = onSnapshot(
      firebaseQuery,
      snapshot => {
        const messagesData: Message[] = []
        snapshot.forEach(doc => {
          const data = doc.data()
          messagesData.push({
            id: doc.id,
            text: data.text,
            senderId: data.senderId,
            senderName: data.senderName,
            timestamp: data.timestamp?.toMillis() || Date.now(),
            read: data.read || false,
          })
        })
        setMessages(messagesData)
        setLoading(false)
        setError(null)
        setRoomExists(true)
      },
      err => {
        console.error('Error fetching messages:', err)
        if (
          err.code === 'permission-denied' ||
          err.message?.includes('not found') ||
          err.code === 'not-found'
        ) {
          setError('This conversation is no longer available.')
          setRoomExists(false)
          setMessages([])
        } else {
          setError('Failed to load messages')
        }
        setLoading(false)
      }
    )

    return () => {
      unsubscribe()
    }
  }, [roomId])

  useEffect(() => {
    if (!roomId) return

    if (!isFirebaseAvailable()) return

    const db = getFirebaseDb()
    if (!db) return

    const roomRef = doc(db, CHATROOM_PATH, roomId)
    const unsubscribe = onSnapshot(
      roomRef,
      snapshot => {
        if (snapshot.exists()) {
          const roomData = snapshot.data()
          const roomChatAdminId = roomData?.chatAdminId
          const isActive = roomData?.isActive !== false

          setRoomChatAdminId(roomChatAdminId || null)

          if (!isActive) {
            setIsAuthorized(false)
            setAuthError(
              isSSAdmin
                ? CHAT_ERRORS.UNAUTHORIZED_ADMIN
                : CHAT_ERRORS.ADMIN_CHANGED
            )
            return
          }

          if (roomChatAdminId) {
            if (isSSAdmin) {
              const authorized = roomChatAdminId === currentUser.id
              setIsAuthorized(authorized)
              setAuthError(authorized ? null : CHAT_ERRORS.UNAUTHORIZED_ADMIN)
            } else {
              const authorized = roomChatAdminId === localStorageChatAdminId
              setIsAuthorized(authorized)
              setAuthError(authorized ? null : CHAT_ERRORS.ADMIN_CHANGED)
            }
          } else {
            setIsAuthorized(true)
            setAuthError(null)
          }
        } else {
          setRoomChatAdminId(null)
          setIsAuthorized(false)
          setAuthError(CHAT_ERRORS.ROOM_NOT_AUTHORIZED)
        }
      },
      err => {
        console.error('Error monitoring room authorization:', err)
        setIsAuthorized(false)
        setAuthError('Failed to verify authorization')
      }
    )

    return () => unsubscribe()
  }, [roomId, currentUser.id, isSSAdmin, localStorageChatAdminId])

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || !roomId) return

      if (!isAuthorized) {
        setError(authError || CHAT_ERRORS.UNAUTHORIZED_ADMIN)
        return
      }

      if (!isFirebaseAvailable()) {
        setError(CHAT_ERRORS.SERVICE_UNAVAILABLE)
        return
      }

      const db = getFirebaseDb()
      if (!db) {
        setError(CHAT_ERRORS.SERVICE_UNAVAILABLE)
        return
      }

      try {
        const roomRef = doc(db, CHATROOM_PATH, roomId)
        const roomDoc = await getDoc(roomRef)

        if (!roomDoc.exists()) {
          setError(CHAT_ERRORS.ROOM_NOT_AUTHORIZED)
          setRoomExists(false)
          setIsAuthorized(false)
          return
        }

        const roomData = roomDoc.data()
        const chatAdminId = roomData?.chatAdminId

        if (chatAdminId) {
          if (isSSAdmin && chatAdminId !== currentUser.id) {
            setError(CHAT_ERRORS.UNAUTHORIZED_ADMIN)
            setIsAuthorized(false)
            return
          }
          if (!isSSAdmin && chatAdminId !== localStorageChatAdminId) {
            setError(CHAT_ERRORS.ADMIN_CHANGED)
            setIsAuthorized(false)
            return
          }
        }

        const messagesRef = collection(
          db,
          CHATROOM_PATH,
          roomId,
          CHAT_PATHSEGMENTS.MESSAGES
        )
        await addDoc(messagesRef, {
          text: text.trim(),
          senderId: currentUser.id,
          senderName: currentUser.name,
          timestamp: serverTimestamp(),
          read: false,
        })

        await updateDoc(roomRef, {
          lastMessage: text.trim(),
          lastMessageTime: serverTimestamp(),
        })
      } catch (err) {
        console.error('Error sending message:', err)
        const error = err as { message?: string; code?: string }
        if (
          error.message?.includes('not found') ||
          error.code === 'not-found' ||
          error.message?.includes('No document to update')
        ) {
          setError('This conversation is no longer available.')
          setRoomExists(false)
        } else {
          setError('Failed to send message')
        }
      }
    },
    [
      roomId,
      currentUser,
      isAuthorized,
      authError,
      isSSAdmin,
      localStorageChatAdminId,
    ]
  )

  const markAsRead = useCallback(async () => {
    if (!roomId) return

    if (!isFirebaseAvailable()) return

    const db = getFirebaseDb()
    if (!db) return

    try {
      const messagesRef = collection(
        db,
        CHATROOM_PATH,
        roomId,
        CHAT_PATHSEGMENTS.MESSAGES
      )
      const firebaseQuery = query(
        messagesRef,
        where(CHAT_FIELDS.SENDERID, '!=', currentUser.id),
        where(CHAT_FIELDS.READ, '==', false)
      )

      const snapshot = await getDocs(firebaseQuery)
      const updates = snapshot.docs.map(document =>
        updateDoc(
          doc(
            db,
            CHATROOM_PATH,
            roomId,
            CHAT_PATHSEGMENTS.MESSAGES,
            document.id
          ),
          {
            read: true,
          }
        )
      )

      await Promise.all(updates)
    } catch (err) {
      console.error('Error marking messages as read:', err)
    }
  }, [roomId, currentUser.id])

  const setTypingIndicator = useCallback(
    async (isTyping: boolean) => {
      if (!roomId) return

      if (!isFirebaseAvailable()) return

      const db = getFirebaseDb()
      if (!db) return

      try {
        const typingRef = doc(
          db,
          CHATROOM_PATH,
          roomId,
          CHAT_PATHSEGMENTS.TYPING,
          currentUser.id
        )

        await setDoc(
          typingRef,
          {
            userId: currentUser.id,
            isTyping,
            timestamp: serverTimestamp(),
          },
          { merge: true }
        )
      } catch (err) {
        console.error('Error updating typing indicator:', err)
      }
    },
    [roomId, currentUser.id]
  )

  useEffect(() => {
    if (!roomId || !chatConfig.showTypingIndicator) return

    if (!isFirebaseAvailable()) return

    const db = getFirebaseDb()
    if (!db) return

    const typingRef = collection(
      db,
      CHATROOM_PATH,
      roomId,
      CHAT_PATHSEGMENTS.TYPING
    )
    const unsubscribe = onSnapshot(typingRef, snapshot => {
      const typingUsers = snapshot.docs
        .filter(doc => {
          const userId = doc.data().userId
          return userId !== currentUser.id
        })
        .some(doc => {
          const isTyping = doc.data().isTyping === true

          return isTyping
        })
      setTyping(typingUsers)
    })

    return () => unsubscribe()
  }, [roomId, currentUser.id])

  useEffect(() => {
    const clearTyping = () => {
      if (roomId && currentUser.id) {
        if (!isFirebaseAvailable()) return

        const db = getFirebaseDb()
        if (!db) return

        const typingRef = doc(
          db,
          CHATROOM_PATH,
          roomId,
          CHAT_PATHSEGMENTS.TYPING,
          currentUser.id
        )
        setDoc(
          typingRef,
          {
            userId: currentUser.id,
            isTyping: false,
            timestamp: serverTimestamp(),
          },
          { merge: true }
        ).catch(err => {
          console.error('Error clearing typing indicator:', err)
        })
      }
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTyping()
      }
    }

    const handleBeforeUnload = () => {
      clearTyping()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      clearTyping()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [roomId, currentUser.id])

  return {
    messages,
    loading,
    error,
    typing,
    sendMessage,
    markAsRead,
    setTypingIndicator,
    roomExists,
    isAuthorized,
    authError,
    roomChatAdminId,
  }
}
